import { useEffect, useState } from "react";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useStations } from "@/hooks/useStations";
import { useAuthContext } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AutoGrowTextarea from "./auto-grow-textarea";
import { Mail, Sparkles, Loader2, AlertTriangle, ArrowLeft, Check, Copy as CopyIcon } from "lucide-react";
import type { Sponsor } from "@shared/schema";

/**
 * Workbench-embedded VBR generation, styled to the workbench design language:
 * accent-bar section shell, tinted icon header, open underline fields, and a
 * document-style result panel. Calls the SAME station-scoped endpoints as the
 * classic VBR Engine and tags results with sponsorId.
 */

interface VbrToolProps {
  sponsor: Sponsor;
}

type Step = "form" | "discovering" | "facts" | "generating" | "done";

const UNDERLINE_INPUT = "border-0 border-b border-gray-100 rounded-none px-0 focus-visible:ring-0 focus-visible:border-blue-400 text-[15px] placeholder:text-gray-300";

function FieldLabel({ htmlFor, children }: { htmlFor?: string; children: React.ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 block mb-0.5">
      {children}
    </label>
  );
}

export default function VbrTool({ sponsor }: VbrToolProps) {
  const { getAuthToken } = useAuthContext();
  const { toast } = useToast();
  const activityKey = `/api/sponsors/${sponsor.id}/activity`;

  // Role-aware station list: admins see all, regular users only assigned.
  const { data: stations = [] } = useStations();

  const [stationId, setStationId] = useState<string>("");
  const [industry, setIndustry] = useState("");

  // Most users have exactly one assigned station; preselect it so Generate is
  // immediately available. Never overrides a choice already made.
  useEffect(() => {
    if (!stationId && stations.length === 1) {
      setStationId(stations[0].id);
    }
  }, [stations, stationId]);
  const [targetAudience, setTargetAudience] = useState("");
  const [additionalContext, setAdditionalContext] = useState("");

  const [step, setStep] = useState<Step>("form");
  const [progress, setProgress] = useState("");
  const [facts, setFacts] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [copied, setCopied] = useState(false);

  const input = {
    companyName: sponsor.name,
    companyWebsite: sponsor.website || "",
    industry: industry.trim(),
    targetAudience: targetAudience.trim(),
    painPoints: "",
    additionalContext: additionalContext.trim(),
  };

  const discoverFacts = async () => {
    setError(null);
    setFacts([]);
    setResult(null);
    setStep("discovering");
    setProgress("Analyzing the company and searching the knowledge base...");
    try {
      const token = await getAuthToken();
      const headers: HeadersInit = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000);
      const response = await fetch(`/api/stations/${stationId}/vbr/discover-facts`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify(input),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok) throw new Error("Fact discovery failed");

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response stream");
      const decoder = new TextDecoder();
      let buffer = "";
      let complete: any = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6).trim();
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.type === "progress") {
              setProgress(parsed.detail || parsed.stage || "Working...");
            } else if (parsed.type === "complete") {
              complete = parsed;
            } else if (parsed.type === "error") {
              throw new Error(parsed.message || "Fact discovery failed");
            }
          } catch (e) {
            if (e instanceof Error && !e.message.includes("JSON")) throw e;
          }
        }
      }

      const found = complete?.facts ?? [];
      if (!found.length) {
        throw new Error("No relevant facts were found for this company in the selected station's knowledge base.");
      }
      setFacts(found);
      setStep("facts");
    } catch (err) {
      setError((err as Error).message || "Fact discovery failed");
      setStep("form");
    }
  };

  const generateVbr = async (fact: any) => {
    setError(null);
    setStep("generating");
    setProgress("Generating the VBR email from the selected fact...");
    try {
      const response = await apiRequest(`/api/stations/${stationId}/vbr/regenerate`, "POST", {
        input,
        selectedFact: fact,
        sponsorId: sponsor.id,
        // Stored with the VBR so it can be regenerated later with a
        // different fact from this same discovery set.
        allFacts: facts,
      });
      setResult(response);
      setStep("done");
      queryClient.invalidateQueries({ queryKey: [activityKey] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/activities"] });
      queryClient.invalidateQueries({ queryKey: ["/api/leaderboard"] });
    } catch (err) {
      setError((err as Error).message || "VBR generation failed");
      setStep("facts");
    }
  };

  const copyResult = async () => {
    try {
      await navigator.clipboard.writeText(result?.vbrText ?? "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Copy failed", description: "Select and copy manually.", variant: "destructive" });
    }
  };

  const busy = step === "discovering" || step === "generating";

  return (
    <section className="relative overflow-hidden rounded-xl bg-white shadow-sm border border-gray-100 p-6 sm:p-8">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" />

      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <div className="h-9 w-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
          <Mail className="h-[18px] w-[18px]" />
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Value-Based Recommendation</p>
          <h3 className="text-lg font-semibold text-gray-900 leading-snug">Generate a VBR for {sponsor.name}</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Pick a partner station, discover audience facts, choose the strongest one, and get a ready-to-send email. Saved to this sponsor's history.
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-5">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {step === "form" && (
        <div className="max-w-3xl space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div>
              <FieldLabel>Partner Station <span className="text-red-400 normal-case">*</span></FieldLabel>
              <Select value={stationId} onValueChange={setStationId}>
                <SelectTrigger className="mt-1" data-testid="select-vbr-station">
                  <SelectValue placeholder="Select a station" />
                </SelectTrigger>
                <SelectContent>
                  {stations.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <FieldLabel htmlFor="vbr-industry">Industry</FieldLabel>
              <Input
                id="vbr-industry"
                placeholder="e.g., Floral e-commerce"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className={UNDERLINE_INPUT}
                data-testid="input-vbr-industry"
              />
            </div>
            <div className="md:col-span-2">
              <FieldLabel htmlFor="vbr-audience">Target Audience</FieldLabel>
              <Input
                id="vbr-audience"
                placeholder="Who should this VBR speak to?"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                className={UNDERLINE_INPUT}
                data-testid="input-vbr-audience"
              />
            </div>
            <div className="md:col-span-2">
              <FieldLabel htmlFor="vbr-context">Additional Context</FieldLabel>
              <AutoGrowTextarea
                id="vbr-context"
                value={additionalContext}
                onChange={setAdditionalContext}
                placeholder="Anything the VBR should account for..."
                focusColor="focus:border-blue-400"
                testId="input-vbr-context"
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-400">
              Company: {sponsor.name}{sponsor.website ? ` (${sponsor.website})` : ""} - from this sponsor's profile.
            </p>
            <Button
              onClick={discoverFacts}
              disabled={!stationId}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="button-discover-facts"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Discover Facts
            </Button>
          </div>
        </div>
      )}

      {busy && (
        <div className="flex items-center gap-3 rounded-lg bg-blue-50/60 px-4 py-5 text-sm text-blue-700">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          {progress}
        </div>
      )}

      {step === "facts" && (
        <div className="max-w-3xl space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Choose the fact to build the VBR around</p>
            <Button variant="ghost" size="sm" onClick={() => setStep("form")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          </div>
          <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1">
            {facts.map((fact, i) => (
              <button
                key={i}
                onClick={() => generateVbr(fact)}
                className="w-full text-left rounded-lg border border-gray-100 bg-white p-4 hover:border-blue-300 hover:shadow-sm transition-all"
                data-testid={`vbr-fact-${i}`}
              >
                <div className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <div>
                    <p className="text-[15px] leading-relaxed text-gray-900">{fact.fact ?? fact.text ?? String(fact)}</p>
                    {fact.source?.text && (
                      <p className="text-xs text-gray-400 mt-1">Source: {fact.source.text}</p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === "done" && result && (
        <div className="max-w-3xl space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-green-700 flex items-center gap-1.5">
              <Check className="h-4 w-4" /> VBR generated and saved to this sponsor's history.
            </p>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={copyResult} data-testid="button-copy-new-vbr">
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <CopyIcon className="h-4 w-4" />}
              </Button>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => { setStep("facts"); setResult(null); }} data-testid="button-pick-different-fact">
                Pick a different fact
              </Button>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => { setStep("form"); setResult(null); }}>
                New VBR
              </Button>
            </div>
          </div>
          <div className="rounded-lg bg-gray-50 p-6">
            <p className="text-[15px] leading-relaxed whitespace-pre-wrap text-gray-900">{result.vbrText}</p>
          </div>
          {result.modelInfo?.modelUsed && (
            <p className="text-xs text-gray-400">Model: {result.modelInfo.modelUsed}</p>
          )}
        </div>
      )}
    </section>
  );
}
